import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "wifi-ne-fonctionne-plus",
  silo: "reseau",
  h1: "WiFi ne fonctionne plus sur mon ordinateur : 8 solutions à tester",
  metaTitle: "WiFi qui ne marche plus sur PC : solutions efficaces",
  metaDescription: "Votre PC ne se connecte plus au WiFi ? Carte réseau, pilotes, box... Voici les 8 solutions les plus efficaces pour rétablir votre connexion.",
  excerpt: "Votre PC ne voit plus le réseau WiFi, ou il se connecte mais sans accès à internet ? Ce problème courant a plusieurs causes distinctes -- et presque toujours une solution simple à portée de main.",
  publishedAt: "2024-01-20",
  readingMinutes: 6,
  relatedSlugs: ["internet-lent-instable", "mise-a-jour-windows-bloquee", "erreurs-windows-frequentes"],
  faq: [
    {
      question: "Mon WiFi est connecté mais internet ne fonctionne pas -- pourquoi ?",
      answer: "C'est souvent un problème DNS ou un conflit d'adresse IP. La solution la plus simple : ouvrez l'invite de commandes et tapez 'ipconfig /flushdns' puis 'ipconfig /release' et 'ipconfig /renew'. Redémarrez ensuite votre PC.",
    },
    {
      question: "Le WiFi fonctionne sur mon téléphone mais pas sur mon PC -- d'où vient le problème ?",
      answer: "Si d'autres appareils se connectent sans problème, le problème est localisé sur votre PC. C'est probablement un pilote WiFi à mettre à jour, une configuration réseau incorrecte, ou la carte WiFi qui commence à défaillir.",
    },
    {
      question: "Réinstaller Windows réglerait-il mon problème de WiFi ?",
      answer: "C'est possible mais extrême. Dans 95% des cas, une mise à jour ou réinstallation du pilote WiFi suffit. Réinstaller Windows n'est pas nécessaire.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Votre PC ne détecte plus aucun réseau WiFi, ou il voit le réseau mais ne peut pas s'y connecter ? Avant d'appeler votre opérateur ou d'acheter un adaptateur, parcourez ces 8 solutions dans l'ordre -- l'une d'elles résoudra très probablement votre problème.",
    },
    {
      type: "h2",
      text: "Solution 1 : redémarrer la box ET le PC",
    },
    {
      type: "p",
      text: "La solution la plus basique, mais souvent efficace. Éteignez votre PC, débranchez votre box 30 secondes, rebranchez-la, attendez qu'elle démarre complètement (2-3 minutes), puis rallumez votre PC.",
    },
    {
      type: "h2",
      text: "Solution 2 : activer/désactiver le mode avion",
    },
    {
      type: "p",
      text: "Dans le centre de notifications Windows (icône en bas à droite), activez le mode avion puis désactivez-le immédiatement. Cela force la réinitialisation de la carte réseau sans fil.",
    },
    {
      type: "h2",
      text: "Solution 3 : mettre à jour ou réinstaller le pilote WiFi",
    },
    {
      type: "p",
      text: "C'est souvent la solution. Clic droit sur le bouton Démarrer > Gestionnaire de périphériques > Cartes réseau. Faites un clic droit sur votre adaptateur WiFi et choisissez 'Mettre à jour le pilote'. Si ça ne change rien, désinstallez puis réinstallez le pilote.",
    },
    {
      type: "h2",
      text: "Solution 4 : réinitialiser les paramètres réseau",
    },
    {
      type: "p",
      text: "Ouvrez l'invite de commandes en administrateur et tapez ces commandes l'une après l'autre :\n• netsh winsock reset\n• netsh int ip reset\n• ipconfig /flushdns\nRedémarrez votre PC après.",
    },
    {
      type: "h2",
      text: "Solution 5 : oublier le réseau et s'y reconnecter",
    },
    {
      type: "p",
      text: "Allez dans Paramètres > Réseau et Internet > WiFi > Gérer les réseaux connus. Cliquez sur votre réseau et choisissez 'Oublier'. Reconnectez-vous ensuite en saisissant le mot de passe à nouveau.",
    },
    {
      type: "h2",
      text: "Solution 6 : vérifier les [mises à jour Windows](slug:mise-a-jour-windows-bloquee)",
    },
    {
      type: "p",
      text: "Une mise à jour Windows mal installée peut désactiver la carte réseau. Inversement, installer les mises à jour manquantes peut résoudre le problème. Vérifiez dans Paramètres > Windows Update.",
    },
    {
      type: "h2",
      text: "Solution 7 : utiliser l'outil de dépannage réseau",
    },
    {
      type: "p",
      text: "Windows propose un outil de dépannage intégré. Clic droit sur l'icône réseau dans la barre des tâches > Dépanner les problèmes. Windows diagnostique et tente de corriger automatiquement le problème.",
    },
    {
      type: "h2",
      text: "Solution 8 : adaptateur WiFi externe USB",
    },
    {
      type: "p",
      text: "Si la carte WiFi interne est défaillante, un adaptateur WiFi USB (disponible pour 10 à 20 euros) est une solution rapide et peu coûteuse. Branchez-le, installez le pilote fourni, et votre connexion est rétablie.",
    },
    {
      type: "callout",
      variant: "info",
      title: "Connexion Ethernet en attendant",
      text: "Pour travailler en attendant de résoudre le problème WiFi, utilisez un câble Ethernet branché directement sur votre box. C'est plus rapide et plus stable que le WiFi de toute façon.",
    },
    {
      type: "h2",
      text: "Si le problème persiste",
    },
    {
      type: "p",
      text: "Si après ces 8 solutions le WiFi ne fonctionne toujours pas, nos techniciens peuvent analyser à distance votre configuration réseau et identifier le problème. Une connexion WiFi lente une fois rétablie peut aussi être améliorée -- consultez notre guide sur l'[internet lent](slug:internet-lent-instable).",
    },
  ],
};

export default article;
